import {Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, TrackByFunction} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators, ɵFormGroupRawValue, ɵTypedOrUntyped} from '@angular/forms';
import {
    ContactDetailsRequest,
    ContactDetailsRequestV2,
    ContactDetailsResponse,
    DocumentAttachment,
    IncidentResponse,
} from '@npck/af-frontend-api';
import {getFormattedDateOrNull, Spinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, of, Subject, takeUntil} from 'rxjs';
import {DateForm} from '../../../../components/date-picker-field/date-picker-field.component';
import {AttachmentsForm, createAttachmentsRequest, createAttachmentsRequestV2} from '../../../../components/files/files.component';
import {getValidPhoneNumberOrNull, PhoneInfo} from '../../../../domain/phone-utils';
import {DateTimeValidators} from '../../../../infra/date-time.validator';
import {ipValidator} from '../../../../infra/ip.validator';
import {urlValidator} from '../../../../infra/url.validator';

export const OTHER_FIELD_MAX_LENGTH = 100;
export const DETAILS_MAX_LENGTH = 500;
export const CONTACT_REASON_OTHER_MAX_LENGTH = 2000;
export const VICTIM_ACTION_OTHER_MAX_LENGTH = 2000;

export const OTHER_VALUE = 'OTHER';

export const CONTACT_TYPES_WITH_MSISDN = ['CALL', 'SMS', 'MESSENGER'];
export const CONTACT_TYPES_WITH_SUSPICIOUS_LINKS = ['SMS', 'EMAIL', 'MESSENGER', 'SOCIAL', 'INTERNET'];
export const CONTACT_TYPES_WITH_INLINE_FRAUDSTER_ROLE = ['CALL', 'SMS'];

const CONTACT_TYPE_VALIDATORS: Record<string, ContactTypeValidatorConfig> = {
    CALL: {
        requiredFields: ['callContactType'],
        detailsRequired: false,
        showDetailsTextarea: false,
        msisdnRequired: true,
        linksEnabled: false,
    },
    MESSENGER: {
        requiredFields: ['messengerContactSubtype', 'messengerContactType'],
        detailsRequired: false,
        showDetailsTextarea: false,
        msisdnRequired: true,
        linksEnabled: true,
    },
    EMAIL: {
        requiredFields: ['emailContactType'],
        detailsRequired: false,
        showDetailsTextarea: false,
        msisdnRequired: false,
        linksEnabled: true,
    },
    SOCIAL: {
        requiredFields: ['socialContactType'],
        detailsRequired: false,
        showDetailsTextarea: false,
        msisdnRequired: false,
        linksEnabled: true,
    },
    INTERNET: {
        requiredFields: ['internetContactType'],
        detailsRequired: false,
        showDetailsTextarea: false,
        msisdnRequired: false,
        linksEnabled: true,
    },
    SMS: {
        requiredFields: [],
        detailsRequired: true,
        showDetailsTextarea: true,
        msisdnRequired: true,
        linksEnabled: true,
    },
};

const DEFAULT_CONTACT_TYPE_CONFIG: ContactTypeValidatorConfig = {
    requiredFields: [],
    detailsRequired: true,
    showDetailsTextarea: true,
    msisdnRequired: false,
    linksEnabled: false,
};

export function hasDetailsTextarea(contactType: string): boolean {
    return (CONTACT_TYPE_VALIDATORS[contactType] ?? DEFAULT_CONTACT_TYPE_CONFIG).showDetailsTextarea;
}

const CONTACT_TYPE_FIELDS: Record<string, [keyof ContactForm, keyof ContactForm][]> = {
    CALL: [['callContactType', 'callContactTypeOther']],
    MESSENGER: [['messengerContactSubtype', 'messengerContactSubtypeOther'], ['messengerContactType', 'messengerContactTypeOther']],
    EMAIL: [['emailContactType', 'emailContactTypeOther']],
    SOCIAL: [['socialContactType', 'socialContactTypeOther']],
    INTERNET: [['internetContactType', 'internetContactTypeOther']],
};

const ALL_TYPE_SPECIFIC_FIELDS: (keyof ContactForm)[] =
    Object.values(CONTACT_TYPE_FIELDS).flat().flatMap(([main, other]) => [main, other]);

@Component({
    selector: 'af-contact-details-step',
    templateUrl: './contact-details-step.component.html',
    standalone: false,
})
export class ContactDetailsStepComponent implements OnInit, OnDestroy {
    @Input() form: FormGroup<ContactDetailsForm>;
    @Input() incident: IncidentResponse;
    @Input() mode: 'create' | 'edit';
    @Input() contactDetailsResponse: ContactDetailsResponse;
    @Input() contactDetailsResponses: ContactDetailsResponse[];
    @Input() hideDefaultButtons = false;
    @Input() canDelete = true;
    @Input() phoneDisabled = false;
    @Input() contactTypeOptional = false;
    @Output() nextStep = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();
    Spinner = Spinner;
    msisdnPredefinedValue: string;
    existingAttachmentsMap = new Map<FormGroup<ContactForm>, DocumentAttachment[]>();
    private destroyRef = inject(DestroyRef);
    private contactDestroyMap = new Map<FormGroup<ContactForm>, Subject<void>>();

    constructor(
        private formBuilder: FormBuilder,
        private dialogService: DialogService,
    ) {
    }

    get showDeleteButton(): boolean {
        const contactsCount = this.form?.controls?.contacts?.controls?.length ?? 0;
        return this.canDelete && contactsCount > 1 && !this.Spinner.isShown('initForms');
    }

    ngOnInit(): void {
        if (!this.form.controls.contacts) {
            this.form.addControl('contacts', this.formBuilder.array([] as FormGroup<ContactForm>[]));
        }
        if (this.form.controls.contacts.length === 0) {
            if (this.contactDetailsResponses?.length) {
                this.contactDetailsResponses.forEach(response => this.createContactForm(response));
            } else {
                this.createContactForm(this.mode === 'edit' ? this.contactDetailsResponse : null);
            }
        }
    }

    ngOnDestroy(): void {
        const contacts = this.form.controls.contacts;
        if (!contacts || contacts.length === 0) {
            return;
        }
        const allPristine = contacts.controls.every(contactForm => contactForm.pristine);
        if (allPristine) {
            contacts.controls.forEach(contactForm => this.destroyContactSubscriptions(contactForm));
            contacts.clear();
        }
    }

    trackByContactIndex: TrackByFunction<FormGroup<ContactForm>> = (index: number) => index;

    createContactForm(response?: ContactDetailsResponse): void {
        const contactDate = response?.contactDate ? new Date(response.contactDate) : null;
        const victimAppealDate = response?.victimAppealDate ? new Date(response.victimAppealDate) : null;

        const contactForm = this.formBuilder.group<ContactForm>({
            id: new FormControl(response?.id ?? null),
            contactType: new FormControl(response?.contactType ?? null, this.contactTypeOptional ? [] : [Validators.required]),
            callContactType: new FormControl(response?.callContactType ?? null),
            callContactTypeOther: new FormControl(
                response?.callContactTypeOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)],
            ),
            msisdn: new FormControl(null),
            selectedMsisdnInfo: new FormControl(null),
            contactDateTime: this.formBuilder.group<DateForm>({
                date: new FormControl(contactDate),
                time: new FormControl(this.formatTime(contactDate)),
            }),
            messengerContactSubtype: new FormControl(response?.messengerContactSubtype ?? null),
            messengerContactSubtypeOther: new FormControl(
                response?.messengerContactSubtypeOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)],
            ),
            messengerContactType: new FormControl(response?.messengerContactType ?? null),
            messengerContactTypeOther: new FormControl(
                response?.messengerContactTypeOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)],
            ),
            suspiciousLinks: this.formBuilder.array(
                response?.suspiciousLinks?.length
                    ? response.suspiciousLinks.map(link => this.createLinkControl(link))
                    : [this.createLinkControl()],
            ),
            emailContactType: new FormControl(response?.emailContactType ?? null),
            emailContactTypeOther: new FormControl(
                response?.emailContactTypeOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)],
            ),
            socialContactType: new FormControl(response?.socialContactType ?? null),
            socialContactTypeOther: new FormControl(
                response?.socialContactTypeOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)],
            ),
            internetContactType: new FormControl(response?.internetContactType ?? null),
            internetContactTypeOther: new FormControl(
                response?.internetContactTypeOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)],
            ),
            contactTimeframe: new FormControl(response?.contactTimeframe ?? null, [Validators.required]),
            contactReason: new FormControl(response?.contactReason ?? null),
            contactReasonOther: new FormControl(
                response?.contactReasonOther ?? null, [Validators.maxLength(CONTACT_REASON_OTHER_MAX_LENGTH)],
            ),
            victimActions: new FormControl(response?.victimActions as string[] ?? null, [Validators.required]),
            victimActionOther: new FormControl(
                response?.victimActionOther ?? null, [Validators.maxLength(VICTIM_ACTION_OTHER_MAX_LENGTH)],
            ),
            fraudsterRole: new FormControl(response?.fraudsterRole ?? null),
            fraudsterRoleOther: new FormControl(response?.fraudsterRoleOther ?? null, [Validators.maxLength(OTHER_FIELD_MAX_LENGTH)]),
            ipFrom: new FormControl(response?.ipFrom ?? null, [ipValidator()]),
            portFrom: new FormControl(response?.portFrom ?? null),
            ipTo: new FormControl(response?.ipTo ?? null, [ipValidator()]),
            portTo: new FormControl(response?.portTo ?? null),
            victimAppealDateTime: this.formBuilder.group<DateForm>({
                date: new FormControl(victimAppealDate),
                time: new FormControl(this.formatTime(victimAppealDate)),
            }),
            details: new FormControl(response?.details ?? null, [Validators.required, Validators.maxLength(DETAILS_MAX_LENGTH)]),
            attachments: this.formBuilder.group({}),
        });

        if (response?.msisdn) {
            this.msisdnPredefinedValue = response.msisdn.startsWith('+') ? response.msisdn : `+${response.msisdn}`;
        }

        if (response?.attachments?.length) {
            this.existingAttachmentsMap.set(contactForm, response.attachments);
        }

        const destroy$ = new Subject<void>();
        this.contactDestroyMap.set(contactForm, destroy$);

        this.setupContactTypeValidation(contactForm, destroy$);
        this.setupOtherFieldsValidation(contactForm, destroy$);
        this.setMsisdnAndDateValidators(contactForm, false, false);
        this.setSuspiciousLinksValidators(contactForm, false);

        if (response?.contactType) {
            this.applyContactTypeValidators(contactForm, response.contactType);
        }

        this.form.controls.contacts.push(contactForm);
    }

    addSuspiciousLink(contactForm: FormGroup<ContactForm>): void {
        const links = contactForm.controls.suspiciousLinks;
        if (links.length < 10) {
            const newLink = this.createLinkControl();
            newLink.setValidators([Validators.required, urlValidator()]);
            links.push(newLink);
        }
    }

    removeSuspiciousLink(contactForm: FormGroup<ContactForm>, index: number): void {
        const links = contactForm.controls.suspiciousLinks;
        if (links.length > 1) {
            links.removeAt(index);
        }
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.nextStep.emit();
    }

    skipStep(): void {
        this.form.controls.contacts.controls.forEach(contactForm => {
            this.destroyContactSubscriptions(contactForm);
        });
        this.form.controls.contacts.clear();
        this.nextStep.emit();
    }

    deleteContact(index: number): void {
        this.dialogService.openDialog({
            title: 'i18n_af_contactDeletionDialogTitle',
            subtitle: 'i18n_af_contactDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {index: `${index + 1}`},
        })
            .pipe(
                mergeMap(dialogResult => dialogResult ? of(dialogResult) : EMPTY),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                const contactForm = this.form.controls.contacts.at(index);
                this.destroyContactSubscriptions(contactForm);
                this.form.controls.contacts.removeAt(index);
            });
    }



    private setupContactTypeValidation(contactForm: FormGroup<ContactForm>, destroy$: Subject<void>): void {
        contactForm.controls.contactType.valueChanges
            .pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.resetTypeSpecificFields(contactForm);
                this.applyContactTypeValidators(contactForm, value);
            });
    }

    private applyContactTypeValidators(contactForm: FormGroup<ContactForm>, contactType: string): void {
        if (!contactType) {
            return;
        }

        const config = CONTACT_TYPE_VALIDATORS[contactType] ?? DEFAULT_CONTACT_TYPE_CONFIG;

        config.requiredFields.forEach(field => {
            contactForm.get(field)?.setValidators([Validators.required]);
        });

        if (config.detailsRequired) {
            contactForm.controls.details.setValidators([Validators.required, Validators.maxLength(DETAILS_MAX_LENGTH)]);
        } else {
            contactForm.controls.details.clearValidators();
        }

        this.setMsisdnAndDateValidators(contactForm, config.msisdnRequired, true);
        this.setSuspiciousLinksValidators(contactForm, config.linksEnabled);
        this.updateTypeSpecificValidators(contactForm);
    }

    private setupOtherFieldsValidation(
        contactForm: FormGroup<ContactForm>, destroy$: Subject<void>,
    ): void {
        const controls = contactForm.controls;

        ([
            [controls.fraudsterRole, controls.fraudsterRoleOther, OTHER_FIELD_MAX_LENGTH],
            [controls.callContactType, controls.callContactTypeOther, OTHER_FIELD_MAX_LENGTH],
            [controls.messengerContactType, controls.messengerContactTypeOther, OTHER_FIELD_MAX_LENGTH],
            [controls.internetContactType, controls.internetContactTypeOther, OTHER_FIELD_MAX_LENGTH],
            [controls.messengerContactSubtype, controls.messengerContactSubtypeOther, OTHER_FIELD_MAX_LENGTH],
            [controls.emailContactType, controls.emailContactTypeOther, OTHER_FIELD_MAX_LENGTH],
            [controls.socialContactType, controls.socialContactTypeOther, OTHER_FIELD_MAX_LENGTH],
            [controls.contactReason, controls.contactReasonOther, CONTACT_REASON_OTHER_MAX_LENGTH],
        ] as [FormControl, FormControl, number][]).forEach(([source, other, maxLen]) =>
            this.setupOtherToggle(source, other, maxLen, destroy$),
        );

        controls.victimActions.valueChanges
            .pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                const hasOther = Array.isArray(value) && value.includes(OTHER_VALUE);
                if (hasOther) {
                    controls.victimActionOther.setValidators([
                        Validators.required,
                        Validators.maxLength(VICTIM_ACTION_OTHER_MAX_LENGTH),
                    ]);
                } else {
                    controls.victimActionOther.clearValidators();
                    if (controls.victimActionOther.value) {
                        controls.victimActionOther.reset();
                    }
                }
                controls.victimActionOther.updateValueAndValidity({emitEvent: false});
            });
    }

    private setupOtherToggle(
        source: FormControl<string>,
        target: FormControl<string>,
        maxLength: number,
        destroy$: Subject<void>,
    ): void {
        source.valueChanges
            .pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                if (value === OTHER_VALUE) {
                    target.setValidators([Validators.required, Validators.maxLength(maxLength)]);
                } else {
                    target.clearValidators();
                    if (target.value) {
                        target.reset();
                    }
                }
                target.updateValueAndValidity({emitEvent: false});
            });
    }

    private createLinkControl(value?: string): FormControl<string> {
        return new FormControl(value ?? 'https://');
    }

    private formatTime(date: Date | null): string | null {
        if (!date) {
            return null;
        }
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }

    private resetTypeSpecificFields(contactForm: FormGroup<ContactForm>): void {
        const extraResetFields: (keyof ContactForm)[] = ['msisdn', 'selectedMsisdnInfo', 'details'];

        [...ALL_TYPE_SPECIFIC_FIELDS, ...extraResetFields].forEach(name => {
            const control = contactForm.controls[name];
            if (control instanceof FormControl) {
                control.clearValidators();
                control.reset();
            }
        });

        contactForm.controls.contactDateTime.controls.date.clearValidators();
        contactForm.controls.contactDateTime.controls.time.clearValidators();
        contactForm.controls.contactDateTime.clearValidators();
        contactForm.controls.contactDateTime.reset();

        while (contactForm.controls.suspiciousLinks.length > 1) {
            contactForm.controls.suspiciousLinks.removeAt(1);
        }
        contactForm.controls.suspiciousLinks.at(0).setValue('https://');
    }

    private updateTypeSpecificValidators(contactForm: FormGroup<ContactForm>): void {
        const controlNames: (keyof ContactForm)[] = [
            'callContactType', 'messengerContactSubtype', 'messengerContactType',
            'emailContactType', 'socialContactType', 'internetContactType', 'details',
        ];
        controlNames.forEach(name => {
            (contactForm.controls[name] as FormControl)?.updateValueAndValidity({emitEvent: false});
        });
    }

    private setMsisdnAndDateValidators(
        contactForm: FormGroup<ContactForm>,
        msisdnRequired: boolean,
        dateRequired: boolean,
    ): void {
        if (msisdnRequired) {
            contactForm.controls.msisdn.setValidators([Validators.required]);
        } else {
            contactForm.controls.msisdn.clearValidators();
        }
        if (dateRequired) {
            contactForm.controls.contactDateTime.controls.date.setValidators([Validators.required]);
            contactForm.controls.contactDateTime.controls.time.setValidators([Validators.required]);
            contactForm.controls.contactDateTime.setValidators(
                DateTimeValidators.dateTimeValid<DateForm>('date', 'time'),
            );
        } else {
            contactForm.controls.contactDateTime.controls.date.clearValidators();
            contactForm.controls.contactDateTime.controls.time.clearValidators();
            contactForm.controls.contactDateTime.clearValidators();
        }
        contactForm.controls.msisdn.updateValueAndValidity({emitEvent: false});
        contactForm.controls.contactDateTime.controls.date.updateValueAndValidity({emitEvent: false});
        contactForm.controls.contactDateTime.controls.time.updateValueAndValidity({emitEvent: false});
        contactForm.controls.contactDateTime.updateValueAndValidity({emitEvent: false});
    }

    private setSuspiciousLinksValidators(contactForm: FormGroup<ContactForm>, enabled: boolean): void {
        contactForm.controls.suspiciousLinks.controls.forEach(linkControl => {
            if (enabled) {
                linkControl.setValidators([Validators.required, urlValidator()]);
            } else {
                linkControl.clearValidators();
            }
            linkControl.updateValueAndValidity({emitEvent: false});
        });
        contactForm.controls.suspiciousLinks.updateValueAndValidity({emitEvent: false});
    }

    private destroyContactSubscriptions(contactForm: FormGroup<ContactForm>): void {
        const destroy$ = this.contactDestroyMap.get(contactForm);
        if (destroy$) {
            destroy$.next();
            destroy$.complete();
            this.contactDestroyMap.delete(contactForm);
        }
        this.existingAttachmentsMap.delete(contactForm);
    }
}

export type ContactDetailsFormValue = ɵTypedOrUntyped<ContactDetailsForm, ɵFormGroupRawValue<ContactDetailsForm>, any>;

export interface ContactDetailsForm {
    contacts?: FormArray<FormGroup<ContactForm>>;
}

export interface ContactForm {
    id: FormControl<string>;
    contactType: FormControl<string>;
    callContactType: FormControl<string>;
    callContactTypeOther: FormControl<string>;
    msisdn: FormControl<string>;
    selectedMsisdnInfo: FormControl<PhoneInfo>;
    contactDateTime: FormGroup<DateForm>;
    messengerContactSubtype: FormControl<string>;
    messengerContactSubtypeOther: FormControl<string>;
    messengerContactType: FormControl<string>;
    messengerContactTypeOther: FormControl<string>;
    suspiciousLinks: FormArray<FormControl<string>>;
    emailContactType: FormControl<string>;
    emailContactTypeOther: FormControl<string>;
    socialContactType: FormControl<string>;
    socialContactTypeOther: FormControl<string>;
    internetContactType: FormControl<string>;
    internetContactTypeOther: FormControl<string>;
    contactTimeframe: FormControl<string>;
    contactReason: FormControl<string>;
    contactReasonOther: FormControl<string>;
    victimActions: FormControl<string[]>;
    victimActionOther: FormControl<string>;
    fraudsterRole: FormControl<string>;
    fraudsterRoleOther: FormControl<string>;
    ipFrom: FormControl<string>;
    portFrom: FormControl<string>;
    ipTo: FormControl<string>;
    portTo: FormControl<string>;
    victimAppealDateTime: FormGroup<DateForm>;
    details: FormControl<string>;
    attachments: FormGroup<AttachmentsForm>;
}

export function createContactDetailsRequest(contactForm: FormGroup<ContactForm>): ContactDetailsRequest {
    const raw = contactForm.getRawValue();
    const contactType = raw.contactType;

    const activePairs = CONTACT_TYPE_FIELDS[contactType] ?? [];
    const activeFieldSet = new Set(activePairs.flatMap(([main, other]) => [main, other]));
    const typeSpecific: Record<string, string | null> = {};

    for (const field of ALL_TYPE_SPECIFIC_FIELDS) {
        typeSpecific[field] = activeFieldSet.has(field) ? raw[field] as string : null;
    }

    for (const [main, other] of activePairs) {
        if (typeSpecific[main] !== OTHER_VALUE) {
            typeSpecific[other] = null;
        }
    }

    const msisdn = CONTACT_TYPES_WITH_MSISDN.includes(contactType)
        ? getValidPhoneNumberOrNull(raw.msisdn, raw.selectedMsisdnInfo)?.replace('+', '') ?? null
        : null;

    const filteredLinks = CONTACT_TYPES_WITH_SUSPICIOUS_LINKS.includes(contactType)
        ? raw.suspiciousLinks?.filter(link => link && link !== 'https://') ?? []
        : [];

    return <ContactDetailsRequest>{
        id: raw.id,
        contactType,
        contactTimeframe: raw.contactTimeframe,
        victimActions: raw.victimActions,
        ...typeSpecific,
        msisdn,
        contactDate: getFormattedDateOrNull(raw.contactDateTime?.date),
        suspiciousLinks: filteredLinks.length ? filteredLinks : null,
        contactReason: raw.contactReason,
        contactReasonOther: raw.contactReason === OTHER_VALUE ? raw.contactReasonOther : null,
        victimActionOther: raw.victimActions?.includes(OTHER_VALUE) ? raw.victimActionOther : null,
        fraudsterRole: raw.fraudsterRole,
        fraudsterRoleOther: raw.fraudsterRole === OTHER_VALUE ? raw.fraudsterRoleOther : null,
        ipFrom: raw.ipFrom,
        portFrom: raw.portFrom,
        ipTo: raw.ipTo,
        portTo: raw.portTo,
        victimAppealDate: getFormattedDateOrNull(raw.victimAppealDateTime?.date),
        details: hasDetailsTextarea(contactType) ? raw.details : null,
        attachments: createAttachmentsRequest(contactForm.controls.attachments?.value),
    };
}

interface ContactTypeValidatorConfig {
    requiredFields: string[];
    detailsRequired: boolean;
    showDetailsTextarea: boolean;
    msisdnRequired: boolean;
    linksEnabled: boolean;
}

export function createContactDetailsRequestV2(contactForm: FormGroup<ContactForm>): ContactDetailsRequestV2 {
    const contactDetails = createContactDetailsRequest(contactForm);
    return <ContactDetailsRequestV2>{
        ...contactDetails,
        id: undefined,
        portFrom: contactDetails.portFrom ? Number(contactDetails.portFrom) : null,
        portTo: contactDetails.portTo ? Number(contactDetails.portTo) : null,
        attachments: createAttachmentsRequestV2(contactForm.controls.attachments?.value),
    };
}
